import React, { useCallback, useMemo } from "react";

import cn from "classnames";
import { isEqual, pick } from "lodash-es";
import { useIntl } from "react-intl";

import Address from "@mapmycustomers/shared/types/Address";
import { EntityWithLocation } from "@mapmycustomers/shared/types/entity";
import getFormattedAddressForUi from "@mapmycustomers/shared/util/formatters/getFormattedAddressForUi";
import { Button, Popconfirm } from "@mapmycustomers/ui";

import geoAddressToNullableAddress from "@app/util/geoAddressToNullableAddress";

import styles from "./AddressWarning.module.scss";

interface AddressWarningProps {
  addressEditable?: boolean;
  entity: EntityWithLocation;
  loading?: boolean;
  onCancel?: () => void;
  onOk?: (address: Address) => void;
  proposedClassName?: string;
  showCancelConfirmation?: boolean;
}

const AddressWarning = ({
  addressEditable = true,
  entity,
  loading,
  onCancel,
  onOk,
  proposedClassName,
  showCancelConfirmation,
}: AddressWarningProps) => {
  const intl = useIntl();

  const geoAddressAddress = useMemo(
    () => (entity.geoAddress ? geoAddressToNullableAddress(entity.geoAddress, false) : undefined),
    [entity.geoAddress]
  ) as Address; // Address type is not correct, fields should be nullable, will be fixed in scope of TART-19157

  const showRecommendations = useMemo(() => {
    if (!geoAddressAddress) {
      return false;
    }

    const fields: (keyof Address)[] = [
      "address",
      "countryCode",
      "city",
      "regionCode",
      "postalCode",
    ];

    return !isEqual(pick(entity, fields), pick(geoAddressAddress, fields));
  }, [entity, geoAddressAddress]);

  const handleOk = useCallback(() => {
    if (geoAddressAddress) {
      onOk?.(geoAddressAddress);
    }
  }, [geoAddressAddress, onOk]);

  const renderBody = () => {
    if (!showRecommendations) {
      return (
        <div>
          {intl.formatMessage({
            id: "entity.incompleteAddressWarning.popover.body.noRecommendations",
            defaultMessage:
              "We’ve mapped this record using its best available location, but some address details are missing or are inaccurate. For the most accurate placement, fill in all the address fields with valid values.",
            description: "Incomplete address warning popover - body text without recommendations",
          })}
        </div>
      );
    }

    return (
      <>
        <div>
          {intl.formatMessage({
            id: "entity.incompleteAddressWarning.popover.body",
            defaultMessage:
              "We are unable to accurately map this record based on the provided address, but have positioned it based on the best match available.",
            description: "Incomplete address warning popover - body text",
          })}
        </div>
        {addressEditable && (
          <>
            <div>
              {intl.formatMessage({
                id: "entity.incompleteAddressWarning.popover.notice",
                defaultMessage: "Would you like to update to the recommended address above?",
                description: "Incomplete address warning popover - notice",
              })}
            </div>
            <div>
              <span className={cn(styles.proposed, proposedClassName)}>
                {getFormattedAddressForUi(entity.geoAddress)}
              </span>
            </div>
          </>
        )}
      </>
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.body}>{renderBody()}</div>
      {addressEditable && (
        <div className={styles.actions}>
          {showRecommendations && (
            <Button loading={loading} onClick={handleOk} type="primary">
              {intl.formatMessage({
                id: "entity.incompleteAddressWarning.popover.action.update",
                defaultMessage: "Update Address",
                description: "Incomplete address warning popover - Update Address button title",
              })}
            </Button>
          )}

          <Popconfirm
            cancelText={intl.formatMessage({
              id: "editActivityModal.footer.deleteActivity.invalidConfirm.cancel",
              defaultMessage: "Cancel",
              description: "Keep invalid address confirmation text - cancel button",
            })}
            description={intl.formatMessage({
              id: "entity.incompleteAddressWarning.popover.invalidConfirm.text",
              defaultMessage:
                "If you opt to keep the invalid address, the pin’s location will not accurately match the provided address.",
              description: "Keep invalid address confirmation text",
            })}
            disabled={!showCancelConfirmation}
            okText={intl.formatMessage({
              id: "editActivityModal.footer.deleteActivity.invalidConfirm.ok",
              defaultMessage: "Keep Invalid Address",
              description: "Keep invalid address confirmation text - confirm button",
            })}
            onConfirm={onCancel}
            overlayClassName={styles.overlay}
            placement="bottom"
          >
            <Button
              disabled={entity.suppressPartialAddressWarning}
              onClick={showCancelConfirmation ? undefined : onCancel}
            >
              {intl.formatMessage({
                id: "entity.incompleteAddressWarning.popover.action.keep",
                defaultMessage: "Keep Invalid Address",
                description:
                  "Incomplete address warning popover - Keep Invalid Address button title",
              })}
            </Button>
          </Popconfirm>
        </div>
      )}
    </div>
  );
};

export default AddressWarning;
